import { Link } from "@inertiajs/react"
import { ChevronRight, Mail } from "lucide-react"
import { useTranslation } from "react-i18next"

import { StatusBadge } from "@/components/status-badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { toAppLocale } from "@/lib/i18n/locales"
import { formatRelativeIntl } from "@/lib/relative-time"
import { invitationsPath } from "@/routes"
import type { PendingAdminInvite } from "@/types/admin"

/**
 * OKS-12 — Pending OKS-Admin invites digest card on `/admin` Overview.
 *
 * Renders ≤5 rows from `Invitation.pending` (Backend `Admin::OverviewController`
 * scope — expired excluded server-side; `at-a-glance hygiene` per Backend TRD).
 * Per-row UX: email + relative-sent-at + `StatusBadge`. Status will always be
 * `"pending"` here — the digest scope drops expired entries; typo-recovery
 * resend lives on the per-tenant `PendingFirstAdminInviteCard` (which uses
 * `Invitation.live` and surfaces both `pending` + `expired`).
 *
 * OKS-121 — overflow indicator. When the server-provided uncapped total
 * (`total`) exceeds the rendered row count, a right-aligned Inertia
 * `<Link>` to `/invitations` (the OKS-11 live list, `authorized_scope`'d
 * so OKS Admins see every row) renders with copy `"+{N} more"` + a
 * decorative `ChevronRight` icon. The link is conditionally rendered:
 * when `total <= invites.length` no link or extra chrome appears,
 * matching the empty/populated-no-overflow states.
 *
 * Locked QA testids: `admin-pending-admin-digest` on the Card region;
 * rows carry `admin-pending-admin-digest-row` with `data-invitation-id`;
 * the overflow link carries `admin-pending-admin-digest-overflow-link`.
 * The inner `StatusBadge` keeps its default `admin-status-badge` testid.
 */

interface AdminInvitesDigestCardProps {
  /**
   * ≤5 rows. Server-capped (Backend `PENDING_DIGEST_LIMIT = 5`); FE
   * doesn't truncate on its own.
   */
  invites: PendingAdminInvite[]
  /**
   * OKS-121 — uncapped server total over the same scope as `invites`
   * (`Invitation.pending` + `role: "admin"`). FE derives the overflow
   * count as `total - invites.length`; the controller stays unaware of
   * the FE display cap. Always >= `invites.length` server-side, but the
   * component clamps `overflowCount` at zero defensively.
   */
  total: number
}

export function AdminInvitesDigestCard({
  invites,
  total,
}: AdminInvitesDigestCardProps) {
  const { t, i18n } = useTranslation()
  const isEmpty = invites.length === 0
  const overflowCount = Math.max(0, total - invites.length)

  return (
    <Card data-testid="admin-pending-admin-digest">
      <CardHeader>
        <CardTitle className="text-base font-medium">
          {t("admin.invites.digest.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isEmpty ? (
          <div
            className="text-muted-foreground flex items-center gap-2 text-sm"
            data-testid="admin-pending-admin-digest-empty"
          >
            <Mail className="size-4 shrink-0" aria-hidden="true" />
            <span>{t("admin.invites.digest.empty")}</span>
          </div>
        ) : (
          <ul className="flex flex-col gap-2.5" role="list">
            {invites.map((invite) => (
              <li
                key={invite.id}
                role="listitem"
                className="flex items-center gap-3"
                data-testid="admin-pending-admin-digest-row"
                data-invitation-id={invite.id}
              >
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-foreground truncate text-sm font-medium">
                    {invite.email}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {t("admin.invites.card.pending.sent_at", {
                      relative: formatRelativeIntl(
                        invite.sent_at,
                        toAppLocale(i18n.language),
                      ),
                    })}
                  </span>
                </div>
                <StatusBadge status={invite.status} />
              </li>
            ))}
          </ul>
        )}
        {overflowCount > 0 && (
          <Link
            href={invitationsPath()}
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 inline-flex items-center gap-1 self-end rounded-sm py-1.5 text-xs font-medium underline-offset-4 hover:underline focus-visible:ring-[3px] focus-visible:outline-none"
            data-testid="admin-pending-admin-digest-overflow-link"
          >
            {t("admin.invites.digest.overflow_more", { count: overflowCount })}
            <ChevronRight className="size-4" aria-hidden="true" />
          </Link>
        )}
      </CardContent>
    </Card>
  )
}
